import { useNode, useEditor } from "@craftjs/core";
import { getRandomId as getRandomNodeId } from "@craftjs/utils";
import React, { useContext, useEffect } from "react";
import { RichTextInput } from "../../input";
import { ListSettings } from "./settings";
import GrigoraCSSRenderer from "./../css-render";
import {
  pasteHtmlAtCaret,
  setEndOfContenteditable,
} from "../../helpers/richtext/caret-position";
import { Text } from "../text";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { waitForElement, waitForLastElement } from "../../dom";
import { CMSListSettings } from "./cms-settings";
import icon from "./icon";
import { blocksLibrary } from "..";
import classnames from "classnames";
import { getDynamicCSS, getSupportProps } from "../../block-supports";
import CMSIcon from "./cms-icon";

export const List = (props) => {
  const {
    id,
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    id: state.id,
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));

  const { editorDocument = document, screen } =
    useContext(GrigoraDashboardContext) ?? {};

  const {
    query,
    actions,
    query: { node, parseFreshNode },
    actions: { add, selectNode, setProp: setPropGlobal },
  } = useEditor();

  const parents = node(id).ancestors();

  const {
    content = [""],
    listType = "ul",
    convertedFrom,
    convertedText,
  } = props;

  var blockProps = getSupportProps({
    id,
    ...props,
  });

  const wrapperClass = classnames(
    {
      "g-list": true,
      [`list-${id}`]: id,
    },
    blockProps.className,
  );

  const ListType = listType === "sort" ? "ol" : listType;

  function addBlock(
    blockID,
    blockSlug,
    blockProps,
    inPlace,
    focusCaretOnEnd = false,
  ) {
    if (blocksLibrary[blockSlug]) {
      return addBlockInCraftJS(
        blockID,
        blocksLibrary[blockSlug],
        blockProps,
        inPlace,
        blocksLibrary[blockSlug].craft.related.supportNestedBlocks,
        focusCaretOnEnd,
      );
    }

    if (blockSlug === "grigora/heading") {
      actions.setProp(blockID, (props) => {
        props.structureTag = "h2";
        props.content = "";
      });
    }
  }

  function convertToList(blockIDs, type) {
    var blocks = blockIDs.map((blockID) => {
      var blockProps = query.node(blockID).get().data.props ?? {};
      var blockSlug = query.node(blockID).get().data.custom?.slug ?? "";
      return {
        blockProps,
        blockSlug,
        blockID,
      };
    });
    // Capture all the content
    var content = blocks
      .map((block) => {
        if (block.blockSlug === "grigora/text") {
          return [block.blockProps.content];
        } else if (block.blockSlug === "grigora/list") {
          return block.blockProps.content;
        } else {
          return [];
        }
      })
      .flat();
    var newNode = addBlock(
      blocks[0].blockID,
      "grigora/list",
      {
        listType: type,
        content: content,
      },
      false,
    );
    // Delete all the blocks
    blocks.map((block) => {
      actions.delete(block.blockID);
    });
    waitForLastElement(
      editorDocument,
      ".list-" + newNode + " p.grigora-richtext-input__field",
    ).then((elm) => {
      setEndOfContenteditable(editorDocument, elm);
    });
  }

  function addBlockInCraftJS(
    blockID,
    Component,
    blockProps = {},
    inPlace = false,
    canvas = false,
    focusCaretOnEnd = false,
  ) {
    const parents = blockID ? query.node(blockID).ancestors() : undefined;
    const nodeID = getRandomNodeId();
    const freshNode = {
      id: nodeID,
      data: {
        type: Component,
        isCanvas: canvas ? true : undefined,
        props: blockProps,
      },
    };
    const nodeToAdd = query.parseFreshNode(freshNode).toNode();
    var node_parent = undefined;
    var node_index = undefined;
    if (parents && parents.length > 0) {
      node_parent = parents[0];
    }
    if (node_parent) {
      let parentChildrens = query.node(node_parent).childNodes();
      node_index = parentChildrens.indexOf(blockID);
    }
    actions.add(nodeToAdd, node_parent ?? ROOT_NODE, node_index + 1);
    if (inPlace) {
      actions.delete(blockID);
    }
    selectNode(nodeID);
    if (focusCaretOnEnd) {
      waitForElement(
        editorDocument,
        `[data-grigora-block-id="${nodeID}"] .grigora-richtext-input__field`,
      ).then((elm) => {
        setEndOfContenteditable(editorDocument, elm);
      });
    }
    return nodeID;
  }

  const handleOnKeyDown = (e) => {
    if (e.ctrlKey && e.shiftKey) {
      switch (e.keyCode) {
        // for Numbered list
        case 55: {
          var blockID = id;
          var blockProps = query.node(blockID).get().data.props ?? {};
          var blockSlug = query.node(blockID).get().data.custom?.slug ?? "";
          // If its a text block then convert it
          if (blockSlug === "grigora/text") {
            convertToList(blockID, "ol");
          }
          // If its a list then convert it back to text
          else if (blockSlug === "grigora/list") {
            if (blockProps.listType === "ul") {
              actions.setProp(blockID, (props) => {
                props.listType = "ol";
              });
              waitForLastElement(
                editorDocument,
                ".list-" + blockID + " ol p.grigora-richtext-input__field",
              ).then((elm) => {
                setEndOfContenteditable(editorDocument, elm);
              });
            } else {
              var newNode;
              blockProps.content?.map((content) => {
                newNode = addBlock(
                  newNode ?? blockID,
                  "grigora/text",
                  {
                    content: content,
                  },
                  false,
                );
              });
              actions.delete(blockID);
              waitForLastElement(
                editorDocument,
                ".text-" + newNode + " p.grigora-richtext-input__field",
              ).then((elm) => {
                setEndOfContenteditable(editorDocument, elm);
              });
            }
          }

          e.preventDefault();
          break;
        }
        case 56: {
          var blockID = id;
          var blockProps = query.node(blockID).get().data.props ?? {};
          var blockSlug = query.node(blockID).get().data.custom?.slug ?? "";
          // If its a text block then convert it
          if (blockSlug === "grigora/text") {
            convertToList(blockID, "ul");
          }
          // If its a list then convert it back to text
          else if (blockSlug === "grigora/list") {
            if (blockProps.listType === "ol") {
              actions.setProp(blockID, (props) => {
                props.listType = "ul";
              });
              waitForLastElement(
                editorDocument,
                ".list-" + blockID + " ul p.grigora-richtext-input__field",
              ).then((elm) => {
                setEndOfContenteditable(editorDocument, elm);
              });
            } else {
              var newNode;
              blockProps.content?.map((content) => {
                newNode = addBlock(
                  newNode ?? blockID,
                  "grigora/text",
                  {
                    content: content,
                  },
                  false,
                );
              });
              actions.delete(blockID);
              waitForLastElement(
                editorDocument,
                ".text-" + newNode + " p.grigora-richtext-input__field",
              ).then((elm) => {
                setEndOfContenteditable(editorDocument, elm);
              });
            }
          }

          e.preventDefault();
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  return (
    <div
      {...blockProps}
      {...(screen === "cms-post-editor"
        ? { ref: (ref) => connect(ref) }
        : { ref: (ref) => connect(drag(ref)) })}
      onClick={() => selected}
      className={wrapperClass}
      onKeyDown={handleOnKeyDown}
      onFocus={() => {
        selectNode(id);
      }}
      data-grigora-block="true"
      data-grigora-block-id={id}
    >
      <style>{`${getDynamicCSS(id, props)}`}</style>
      <style>{`${GrigoraCSSRenderer.blockCSS__List(id, props)}`}</style>
      <ListType className="c">
        {content.map((item, i, array) => {
          return (
            <li className={`i-${i}`} key={`list-${id}-${i}`}>
              <RichTextInput
                value={content[i]}
                onChange={(e, historyMergeAction) => {
                  if (historyMergeAction) {
                    historyMergeAction.setProp(id, (props) => {
                      const temp = [...content];
                      temp[i] = e;
                      props.content = temp;
                      return props;
                    });
                    return;
                  }
                  setProp((props) => {
                    const temp = [...(props.content ? props.content : [])];
                    temp[i] = e;
                    props.content = temp;
                    return props;
                  }, 500);
                }}
                onMerge={(forward) => {
                  let historyMergeAction = actions.history.merge();

                  if (forward) {
                    // Merge with next list item
                    if (i < array.length - 1) {
                      historyMergeAction.setProp(id, (props) => {
                        const temp = [...content];
                        temp[i] = temp[i] + temp[i + 1];
                        temp.splice(i + 1, 1);
                        props.content = temp;
                        return props;
                      });
                      return;
                    }
                    // Merge with next block
                    if (parents && parents.length > 0) {
                      let parentChildrens = node(parents[0]).childNodes();
                      let node_index = parentChildrens.indexOf(id) + 1;
                      if (parentChildrens[node_index]) {
                        var nodeData = node(parentChildrens[node_index])?.get()
                          .data;

                        // Merge with text block
                        if (
                          nodeData &&
                          nodeData?.custom?.slug === "grigora/text"
                        ) {
                          var forward_node = nodeData?.props?.content ?? "";
                          historyMergeAction.setProp(id, (props) => {
                            const temp = [...content];
                            temp.push(forward_node);
                            props.content = temp;
                            return props;
                          });
                          waitForElement(
                            editorDocument,
                            `.list-${id} .c .i-${
                              i + 1
                            } .grigora-richtext-input .grigora-richtext-input__field`,
                          ).then((elm) => {
                            elm.focus();
                          });
                          historyMergeAction.delete(
                            parentChildrens[node_index],
                          );
                        }

                        // Merge with list block
                        if (
                          nodeData &&
                          nodeData?.custom?.slug === "grigora/list"
                        ) {
                          var forward_node = nodeData?.props?.content ?? [""];
                          historyMergeAction.setProp(id, (props) => {
                            const temp = [...content, ...forward_node];
                            props.content = temp;
                            return props;
                          });
                          waitForElement(
                            editorDocument,
                            `.list-${id} .c .i-${
                              i + 1
                            } .grigora-richtext-input .grigora-richtext-input__field`,
                          ).then((elm) => {
                            elm.focus();
                          });
                          historyMergeAction.delete(
                            parentChildrens[node_index],
                          );
                        }
                      }
                    }
                  } else {
                    var elements = editorDocument.querySelectorAll(
                      `.list-${id} .c .grigora-richtext-input .grigora-richtext-input__field`,
                    );
                    if (i < 1) {
                      // Merge with previous blocks
                      if (parents && parents.length > 0) {
                        let parentChildrens = node(parents[0]).childNodes();
                        let node_index = parentChildrens.indexOf(id) - 1;
                        if (node_index < 0) {
                          return;
                        }
                        if (parentChildrens[node_index]) {
                          var nodeData = node(
                            parentChildrens[node_index],
                          )?.get().data;

                          // Merge with list block
                          if (
                            nodeData &&
                            nodeData?.custom?.slug === "grigora/list"
                          ) {
                            var forward_node = nodeData?.props?.content ?? [""];
                            historyMergeAction.setProp(id, (props) => {
                              const temp = [...forward_node, ...content];
                              props.content = temp;
                              return props;
                            });
                            waitForElement(
                              editorDocument,
                              `.list-${id} .c .i-${forward_node.length} .grigora-richtext-input .grigora-richtext-input__field`,
                            ).then((elm) => {
                              elm.focus();
                            });
                            historyMergeAction.delete(
                              parentChildrens[node_index],
                            );
                            return;
                          }

                          // Merge with anyother block
                          const nodeID = getRandomNodeId();
                          const freshNode = {
                            id: nodeID,
                            data: {
                              type: Text,
                              props: {
                                ...props,
                                content:
                                  convertedFrom === "text"
                                    ? (convertedText ?? "") + content[i]
                                    : content[i],
                                convertedFrom: "list",
                              },
                            },
                          };
                          const nodeToAdd = parseFreshNode(freshNode).toNode();
                          var node_parent = undefined;
                          if (parents && parents.length > 0) {
                            node_parent = parents[0];
                          }
                          if (content.length > 1) {
                            historyMergeAction.setProp(id, (props) => {
                              var temp = [...content];
                              temp = temp.slice(1);
                              props.content = temp;
                              return props;
                            });
                          } else {
                            historyMergeAction.delete(id);
                          }
                          historyMergeAction.add(
                            nodeToAdd,
                            node_parent,
                            node_index + 1,
                          );
                          waitForElement(
                            editorDocument,
                            ".text-" +
                              nodeID +
                              " .grigora-richtext-input__field",
                          ).then((elm) => {
                            if (convertedFrom === "text") {
                              setEndOfContenteditable(editorDocument, elm);
                            } else {
                              elm.focus();
                            }
                          });
                          return;
                        }
                      }

                      return;
                    }
                    elements[i - 1].focus();
                    elements[i - 1].innerHTML = content[i];
                    // setEndOfContenteditable(elements[i-1])
                    pasteHtmlAtCaret(editorDocument, content[i - 1]);
                    historyMergeAction.setProp(id, (props) => {
                      const temp = [...content];
                      if (i > -1) {
                        temp[i - 1] = temp[i - 1] + temp[i];
                        temp.splice(i, 1);
                      }
                      props.content = temp;
                      return props;
                    });
                  }
                }}
                onSplit={(newValue, currentVal, historyMergeAction) => {
                  // Is last element is ref then create a new text block and move to it.
                  if (content[i] === "") {
                    if (content.length - 1 === i) {
                      // split into new text block
                      const nodeID = getRandomNodeId();
                      const freshNode = {
                        id: nodeID,
                        data: {
                          type: Text,
                          props: {
                            ...props,
                            content: newValue.trim(),
                          },
                        },
                      };
                      const nodeToAdd = parseFreshNode(freshNode).toNode();
                      var node_parent = undefined;
                      var node_index = undefined;
                      if (parents && parents.length > 0) {
                        node_parent = parents[0];
                      }
                      if (node_parent) {
                        let parentChildrens = node(node_parent).childNodes();
                        node_index = parentChildrens.indexOf(id) + 1;
                      }
                      if (historyMergeAction) {
                        historyMergeAction.setProp(id, (props) => {
                          const temp = [...content];
                          temp.splice(i);
                          props.content = temp;
                          return props;
                        });
                        historyMergeAction.add(
                          nodeToAdd,
                          node_parent,
                          node_index,
                        );
                      } else {
                        setProp((props) => {
                          const temp = [...content];
                          temp.splice(i);
                          props.content = temp;
                          return props;
                        });
                        add(nodeToAdd, node_parent, node_index);
                      }
                      waitForElement(
                        editorDocument,
                        ".text-" + nodeID + " .grigora-richtext-input__field",
                      ).then((elm) => {
                        elm.focus();
                      });
                      return;
                    } else {
                      // Split into two lists and add text block in between

                      // Next List
                      var nodeID = getRandomNodeId();
                      var freshNode = {
                        id: nodeID,
                        data: {
                          type: List,
                          props: {
                            ...props,
                            content: [...content.slice(i + 1)],
                          },
                        },
                      };
                      var nodeToAdd = parseFreshNode(freshNode).toNode();
                      var node_parent = undefined;
                      var node_index = undefined;
                      if (parents && parents.length > 0) {
                        node_parent = parents[0];
                      }
                      if (node_parent) {
                        let parentChildrens = node(node_parent).childNodes();
                        node_index = parentChildrens.indexOf(id) + 1;
                      }
                      if (historyMergeAction) {
                        historyMergeAction.add(
                          nodeToAdd,
                          node_parent,
                          node_index,
                        );

                        // Current List
                        historyMergeAction.setProp(id, (props) => {
                          const temp = [...content];
                          temp.splice(0, i);
                          props.content = temp;
                          return props;
                        });
                      } else {
                        add(nodeToAdd, node_parent, node_index);

                        // Current List
                        setProp((props) => {
                          const temp = [...content];
                          temp.splice(0, i);
                          props.content = temp;
                          return props;
                        });
                      }

                      // Text Block in Middle
                      var nodeID = getRandomNodeId();
                      var freshNode = {
                        id: nodeID,
                        data: {
                          type: Text,
                          props: {
                            ...props,
                            content: "",
                          },
                        },
                      };
                      var nodeToAdd = parseFreshNode(freshNode).toNode();
                      var node_parent = undefined;
                      var node_index = undefined;
                      if (parents && parents.length > 0) {
                        node_parent = parents[0];
                      }
                      if (node_parent) {
                        let parentChildrens = node(node_parent).childNodes();
                        node_index = parentChildrens.indexOf(id) + 1;
                      }
                      if (historyMergeAction) {
                        historyMergeAction.setProp(id, (props) => {
                          const temp = [...content];
                          temp.splice(i);
                          props.content = temp;
                          return props;
                        });
                        historyMergeAction.add(
                          nodeToAdd,
                          node_parent,
                          node_index,
                        );
                      } else {
                        setProp((props) => {
                          const temp = [...content];
                          temp.splice(i);
                          props.content = temp;
                          return props;
                        });
                        add(nodeToAdd, node_parent, node_index);
                      }
                      waitForElement(
                        editorDocument,
                        ".text-" + nodeID + " .grigora-richtext-input__field",
                      ).then((elm) => {
                        elm.focus();
                      });

                      return;
                    }
                  }
                  if (historyMergeAction) {
                    historyMergeAction.setProp(id, (props) => {
                      const temp = [...content];
                      temp[i] = newValue;
                      temp.splice(i, 0, currentVal);
                      props.content = temp;
                      return props;
                    });
                  } else {
                    setProp((props) => {
                      const temp = [...content];
                      temp[i] = newValue;
                      temp.splice(i, 0, currentVal);
                      props.content = temp;
                      return props;
                    });
                  }

                  waitForElement(
                    editorDocument,
                    `.list-${id} .c .i-${
                      i + 1
                    } .grigora-richtext-input .grigora-richtext-input__field`,
                  ).then((elm) => {
                    elm.focus();
                  });
                }}
                placeholder={"List Item"}
                tagName={"p"}
              />
            </li>
          );
        })}
      </ListType>
    </div>
  );
};

// To Avoid Mangling in Production
List.displayName = "List";

List.craft = {
  name: "List",
  props: {},
  related: {
    settings: ListSettings,
    CMSsettings: CMSListSettings,
    icon: icon,
    CMSIcon: CMSIcon,
    supports: [
      "grigora/entrance-animations",
      "grigora/css-transform-animations",
      "grigora/mouse-movement",
      "grigora/visibility",
      "grigora/position",
      "grigora/sticky",
      "grigora/filter",
      "grigora/custom-class",
      "grigora/custom-id",
    ],
    category: "basic",
    keywords: ["List", "Numbering", "Bullets", "Points"],
  },
  custom: {
    name: "List",
    slug: "grigora/list",
  },
};
